import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import {
  Text,
  Modal,
  ButtonGroup,
  Button,
  Stack,
  Shelf,
  Loader,
} from "@chaser/components";
import AddonOption from "../Option";
import useShortCode from "../../../hooks/useShortCode";
import {
  ADDON_TYPES,
  BillingPeriods,
  Modules,
  addonIds,
  periodToNoticeTime,
} from "../../../util/constants";
import SubscriptionService from "../../../services/subscription-service";
import usePush from "../../../hooks/usePush";
import { formatDate } from "../../../util/time";
import useModuleInfo from "../../../hooks/useModuleInfo";
import { calculateTieredPrice } from "../utils/calculateTieredPrice";
import { pendo } from "../../../util/logging";
import pluralize from "pluralize";
import { toCurrency } from "../../../util";

import styles from "./AddonBaseModal.module.scss";

const periodToTitle = {
  [BillingPeriods.MONTHLY]: "Monthly",
  [BillingPeriods.YEARLY]: "Annual",
};

const AddonBaseModal = ({
  open,
  close,
  onClosed,
  onSuccess,
  defaultOptions,
  title,
  description,
  successToast,
  loading,
  moduleName,
  currentAddon,
  addon = {},
  nextBillingDate,
}) => {
  const [estimate, setEstimate] = useState(null);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const { execute: executeCancelScheduled, loading: isLoadingCancelScheduled } =
    usePush(SubscriptionService.cancelScheduledChange, {
      errorToast: true,
      successToast: "Scheduled change cancelled",
    });

  const addOnType = addon?.type;

  const isFlatFee = addOnType === ADDON_TYPES.FLAT_FEE;
  const isVolume = addOnType === ADDON_TYPES.VOLUME;

  const minQuantity = useMemo(() => {
    if (isFlatFee) return 1;

    if (isVolume && currentAddon?.overages > 0) {
      return currentAddon.overages + defaultOptions.minQuantity;
    }

    return defaultOptions.minQuantity;
  }, [isFlatFee, isVolume, currentAddon, defaultOptions.minQuantity]);

  const proratedQuantity = useMemo(() => {
    if (!showConfirmation || !estimate) return 0;

    if (isFlatFee) return 1;

    return estimate.proratedQuantity;
  }, [showConfirmation, estimate, isVolume, isFlatFee, currentAddon]);

  const shortCode = useShortCode();
  const { refetchSubscriptionData } = useModuleInfo(moduleName, {
    suspense: false,
  });
  const didInitDefaultQuantity = useRef(false);
  const [purchaseQuantity, setPurchaseQuantity] = useState(
    isFlatFee ? 1 : minQuantity,
  );

  useEffect(() => {
    if (!open) return;

    if (isFlatFee) {
      if (purchaseQuantity !== 1) {
        setPurchaseQuantity(1);
      }
      return;
    }

    if (purchaseQuantity == null && minQuantity != null) {
      setPurchaseQuantity(minQuantity);
    }
  }, [open, isFlatFee, minQuantity, purchaseQuantity]);

  const handleCancelScheduledChange = useCallback(
    async (e) => {
      if (e) e.preventDefault();
      if (!addon?.id) return;

      const isUsersAddon =
        addon.id === addonIds.USER || addon.id === addonIds.ADDITIONAL_USERS;
      const moduleNames = isUsersAddon
        ? [Modules.ORGANISATION, Modules.RECEIVABLES]
        : moduleName
          ? [moduleName]
          : [];

      await executeCancelScheduled({
        shortCode,
        addonId: addon.id,
        moduleName,
        moduleNames,
      });
      refetchSubscriptionData();
    },
    [
      addon?.id,
      executeCancelScheduled,
      moduleName,
      refetchSubscriptionData,
      shortCode,
    ],
  );

  useEffect(() => {
    if (didInitDefaultQuantity.current) return;
    if (isFlatFee) return;
    if (currentAddon?.overages > 0) return;

    const maxQuantity = defaultOptions?.maxQuantity;
    const quantityStep = defaultOptions?.quantityStep || 1;

    if (!maxQuantity || maxQuantity <= minQuantity) return;
    if (purchaseQuantity !== minQuantity) return;

    const denom = maxQuantity - minQuantity;
    const target = minQuantity + denom * 0.3;
    const constrained = Math.max(minQuantity, Math.min(maxQuantity, target));
    const steps = Math.round((constrained - minQuantity) / quantityStep);
    const next = minQuantity + steps * quantityStep;

    didInitDefaultQuantity.current = true;
    setPurchaseQuantity(next);
  }, [
    currentAddon?.overages,
    defaultOptions?.maxQuantity,
    defaultOptions?.quantityStep,
    isFlatFee,
    minQuantity,
    purchaseQuantity,
  ]);

  const {
    plan: { period },
    currencyCode,
  } = useModuleInfo(moduleName, { suspense: false });
  const hasTieredPrice = !!defaultOptions.tiers?.length;

  const { execute: fetchEstimate, loading: isEstimating } = usePush(
    SubscriptionService.estimateAddon,
    {
      errorToast: true,
    },
  );

  const { execute, loading: isMutating } = usePush(
    SubscriptionService.addAddOnToSubscription,
    {
      errorToast: true,
      successToast: successToast,
    },
  );

  const handleContinue = useCallback(async () => {
    const [error, data] = await fetchEstimate({
      shortCode,
      id: defaultOptions.id,
      quantity: purchaseQuantity,
      moduleName,
    });

    if (!error && data) {
      setEstimate(data);
      setShowConfirmation(true);
    }
  }, [fetchEstimate, shortCode, defaultOptions.id, purchaseQuantity]);

  const handleBack = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const handleModalClosed = useCallback(() => {
    setShowConfirmation(false);
    if (onClosed) onClosed();
  }, [onClosed]);

  const handleSubmit = useCallback(
    async (id, quantity, proratedQuantity) => {
      const requestPayload = {
        shortCode,
        addonId: id,
        moduleName,
        quantity,
        proratedQuantity,
      };

      const [err] = await execute(requestPayload);
      if (err) return;

      pendo.track("addon_purchased", {
        addonId: id,
        moduleName,
        addonName: addon?.name,
        quantity,
        proratedQuantity,
        proratedAmount: estimate?.proratedAmount,
        currency: estimate?.currency,
        period,
      });

      close();

      if (onSuccess) await onSuccess(requestPayload);
    },
    [close, execute, onSuccess, shortCode],
  );

  return (
    <Modal
      titleClass={styles.addonBaseModalTitle}
      size="medium"
      open={open}
      toggle={close}
      onClosed={handleModalClosed}
      title={showConfirmation ? "Confirm your payment" : title}
    >
      {loading ? (
        <Modal.Body>
          <Loader style={{ margin: "auto" }} size={20} />
        </Modal.Body>
      ) : (
        <div className={styles.modalContent}>
          <div
            className={`${styles.screenContainer} ${
              showConfirmation ? styles.screenActive : styles.screenExit
            }`}
          >
            <Modal.Body>
              <Stack gap="large" alignItems="center">
                <Text textAlign="center">
                  A pro-rata payment of{" "}
                  {toCurrency(
                    estimate?.proratedAmount / 100,
                    estimate?.currency,
                  )}{" "}
                  {`${currencyCode === "GBP" ? "(excl VAT)" : ""}`} is required
                  to activate your{" "}
                  {pluralize(
                    (addon?.name || "addon")?.toLowerCase(),
                    purchaseQuantity,
                  )}{" "}
                  subscription.{" "}
                  {proratedQuantity !== purchaseQuantity ? (
                    <>
                      You'll receive {proratedQuantity} to use this {period},
                      and can start using{" "}
                      {pluralize(
                        (addon?.name || "addon")?.toLowerCase(),
                        proratedQuantity,
                      )}{" "}
                      right away.
                    </>
                  ) : (
                    <>
                      You can start using the{" "}
                      {pluralize(
                        (addon?.name || "addon")?.toLowerCase(),
                        proratedQuantity,
                      )}{" "}
                      right away.
                    </>
                  )}
                </Text>
                <Text textAlign="center">
                  Your subscription will renew on {formatDate(nextBillingDate)},
                  aligned with your main Chaser subscription.
                </Text>
                <table className={styles.addonTable}>
                  <thead>
                    <tr>
                      <th>Add-on</th>
                      <th>Quantity</th>
                      <th>{periodToTitle[period]} cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{addon?.name || "Addon"}</td>
                      <td>{purchaseQuantity}</td>
                      <td>
                        {hasTieredPrice
                          ? `${toCurrency(
                              calculateTieredPrice(
                                purchaseQuantity,
                                defaultOptions.tiers,
                                defaultOptions.price,
                              ),
                              defaultOptions.currency,
                            )}`
                          : `${toCurrency(
                              (defaultOptions.price * purchaseQuantity) / 100,
                              defaultOptions.currency,
                            )}`}
                        / {period}{" "}
                        {`${currencyCode === "GBP" ? "(excl VAT)" : ""}`}
                      </td>
                    </tr>
                  </tbody>
                </table>
                <Text textAlign="center">
                  Add-ons can be cancelled or downgraded with{" "}
                  {periodToNoticeTime[period]} days notice ahead of the renewal
                  date.
                </Text>
                <Text textAlign="center">
                  You can manage or remove add-ons anytime from your add-ons
                  page.
                </Text>
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <ButtonGroup flex={1} justifyContent="center">
                <Button
                  appearance="secondary"
                  onClick={handleBack}
                  data-trackingid="addon-confirm-back"
                >
                  Go back
                </Button>
                <Button
                  intent="chaser"
                  disabled={isMutating || isEstimating}
                  onClick={() =>
                    handleSubmit(
                      defaultOptions.id,
                      purchaseQuantity,
                      proratedQuantity,
                    )
                  }
                  data-trackingid="addon-confirm-purchase"
                >
                  Pay{" "}
                  {toCurrency(
                    estimate?.proratedAmount / 100,
                    estimate?.currency,
                  )}{" "}
                  and confirm
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </div>
          <div
            className={`${styles.screenContainer} ${
              !showConfirmation ? styles.screenActive : styles.screenExit
            }`}
          >
            <Modal.Body>
              <Stack gap="medium" alignItems="center">
                {description && <Text textAlign="center">{description}</Text>}
                <Shelf
                  width="100%"
                  gap="medium"
                  alignItems="flex-end"
                  justifyContent="center"
                >
                  <AddonOption
                    key={defaultOptions.id}
                    {...defaultOptions}
                    type={addon?.type}
                    description={addon?.description}
                    currency={addon?.currency}
                    period={addon?.period}
                    name={addon?.name || "Addon"}
                    minQuantity={minQuantity}
                    maxQuantity={defaultOptions.maxQuantity}
                    quantityStep={defaultOptions.quantityStep}
                    setQuantity={setPurchaseQuantity}
                    quantity={purchaseQuantity}
                    scheduled={currentAddon?.scheduled}
                    onCancelScheduledChange={handleCancelScheduledChange}
                    isLoadingCancel={isLoadingCancelScheduled}
                  />
                </Shelf>
              </Stack>
            </Modal.Body>
            <Modal.Footer>
              <ButtonGroup>
                <Button appearance="secondary" onClick={close}>
                  Cancel
                </Button>
                <Button
                  loading={isEstimating}
                  intent="chaser"
                  onClick={handleContinue}
                  data-trackingid="addon-continue"
                >
                  Continue
                </Button>
              </ButtonGroup>
            </Modal.Footer>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default AddonBaseModal;
